import { hc } from "hono/client";
import { noteVersion } from "@optymyse/suite-ui";
import type { AppType } from "../../shared/types/api";

/**
 * Asks Link's own API, on this page's host with its session cookie, and hands the answer to the kit's noteVersion
 * before anything reads it, a refusal or a failure as much as a success (SuiteUi's README, "A new version"). The API
 * names the version of link-app that answered (`x-app-version`, src/worker/http.ts), and one this page hasn't heard
 * before is a deploy since it opened, which the kit's notice says (src/client/App.tsx). Every request the page makes to
 * the API and /auth goes through here: the typed client below, signing out, and People's lists and changes. What anyone
 * else serves, an organisation's logo from Impa's media, the browser fetches itself, and this never sees.
 */
export async function askApi(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const response = await fetch(input, { ...init, credentials: "same-origin" });
  noteVersion(response);
  return response;
}

// Headers as they come, whatever their form (an object, a Headers or pairs), with the JSON type set.
const customFetch: typeof fetch = (input, init) => {
  const headers = new Headers(init?.headers);
  headers.set("Content-Type", "application/json");
  return askApi(input, { ...init, headers });
};

export const api = hc<AppType>("/", { fetch: customFetch });

export type ApiErrorBody = {
  error: {
    code: string;
    message: string;
    details?: unknown;
    request_id?: string;
  };
};

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: ApiErrorBody,
  ) {
    super(body.error.message);
  }

  /** The field a refusal is about, where it names one (`details.field`). */
  get field(): string | null {
    const details = this.body.error.details;
    return typeof details === "object" && details !== null && "field" in details && typeof details.field === "string"
      ? details.field
      : null;
  }
}

/** An answer's body, or its refusal as an ApiError: any status may come, as the API throws its refusals. */
export async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as Partial<ApiErrorBody>;
    throw new ApiError(res.status, {
      error: {
        code: body.error?.code ?? "unknown",
        message: body.error?.message ?? "Something went wrong. Please try again.",
        ...(body.error?.details === undefined ? {} : { details: body.error.details }),
        ...(body.error?.request_id === undefined ? {} : { request_id: body.error.request_id }),
      },
    });
  }
  return (await res.json()) as T;
}

/** Asked again after a network or server failure, once; a refusal won't change by asking again. */
export function retryOnce(failureCount: number, error: unknown): boolean {
  return !(error instanceof ApiError && error.status < 500) && failureCount < 1;
}
